import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode';

const SCANNER_ID = 'tiffin-camera-scan-box';

async function safeStop(scanner) {
  try {
    if (scanner && scanner.getState() === Html5QrcodeScannerState.SCANNING) {
      await scanner.stop();
    }
  } catch {
    // Camera may already be stopped/never started — safe to ignore.
  }
}

// Shared camera-scanning box used by both the Staff QR Scanner and POS Scanner. Only ever mounted
// while "Camera" mode is selected — mounting/unmounting handles start/stop automatically, so
// switching to Handheld Scanner mode always fully releases the camera (see useScanMode).
export function CameraScanBox({ onScan, scanSession, className = '' }) {
  const handledRef = useRef(false);
  const pendingStopRef = useRef(Promise.resolve());
  const [scanError, setScanError] = useState('');

  useEffect(() => {
    setScanError('');
    handledRef.current = false;
    let cancelled = false;
    let scanner;

    const mount = async () => {
      await pendingStopRef.current;
      if (cancelled) return;
      scanner = new Html5Qrcode(SCANNER_ID);
      try {
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: 250 },
          async (decodedText) => {
            if (handledRef.current) return;
            handledRef.current = true;
            await safeStop(scanner);
            onScan(decodedText);
          },
          () => {},
        );
      } catch {
        if (!cancelled) setScanError('Unable to access camera. Check permissions, or switch to Handheld Scanner mode.');
      }
    };

    const mountPromise = mount();

    return () => {
      cancelled = true;
      pendingStopRef.current = mountPromise.then(() => safeStop(scanner)).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanSession]);

  return (
    <div className={className}>
      <div id={SCANNER_ID} data-testid="camera-scan-box" className="w-full aspect-square bg-obsidian" />
      {scanError && <p className="px-4 pt-3 text-sm text-destructive" data-testid="camera-scan-error">{scanError}</p>}
    </div>
  );
}
